import { maybe } from '@passionware/monads';
import { AtellioFeatureGroupDefinition } from 'v5/features';
import {
  FeatureName,
  FeatureValueById
} from 'v5/platform/feature-management/feature-management';
import { WithServices } from 'v5/platform/ts/services';
import { WithFeatureManagementService } from 'v5/services/front/FeatureManagementService/FeatureManagementService';
import { WithFeatureService } from 'v5/services/front/FeatureService/FeatureService';
import { WithSupportService } from 'v5/services/front/SupportService/SupportService';
import { WithToastService } from 'v5/services/front/ToastService/ToastService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { useFeatureVersionToggle } from '../useFeatureVersionToggle';
import {
  ToggleVersionSwitch,
  ToggleVersionSwitchProps
} from './ToggleVersionSwitch';

export interface FeatureVersionSwitchProps<
  Feature extends FeatureName<AtellioFeatureGroupDefinition>
> extends WithServices<
      [
        WithFeatureService,
        WithFeatureManagementService,
        WithAppScopeService,
        WithToastService,
        WithSupportService
      ]
    >,
    Omit<ToggleVersionSwitchProps, 'services' | 'toggle' | 'featureName'> {
  featureName: Feature;
  newVersionName: FeatureValueById<AtellioFeatureGroupDefinition, Feature>;
  rollbackVersionName: FeatureValueById<AtellioFeatureGroupDefinition, Feature>;
}

/**
 * `ToggleVersionSwitch` bound to two versions of a feature flag, stored in this
 * browser. Renders nothing when `FeatureToggleBanner` would not.
 */
export function FeatureVersionSwitch<
  Feature extends FeatureName<AtellioFeatureGroupDefinition>
>({
  services,
  featureName,
  newVersionName,
  rollbackVersionName,
  ...content
}: FeatureVersionSwitchProps<Feature>) {
  const toggle = useFeatureVersionToggle({
    services,
    featureName,
    newVersionName,
    rollbackVersionName
  });

  if (maybe.isAbsent(toggle)) {
    return null;
  }

  return (
    <ToggleVersionSwitch
      services={services}
      toggle={toggle}
      featureName={featureName}
      {...content}
    />
  );
}
